import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmt, fmtDate } from "@/lib/adminFmt";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, Download, Trash2, Send, UserCog, Radio, X,
} from "lucide-react";

/**
 * Per-user admin drill-down modal. 4 tabs: Profile · Usage · Portfolios · Actions.
 * Actions include impersonate (15-min short-lived JWT), send-push, GDPR JSON
 * export, and cascading hard-delete. Every mutation is audit-logged server-side.
 *
 * Extracted from Admin.jsx into its own file so Admin.jsx stays focused on the
 * hub-and-spoke shell as the admin surface grows.
 */
export default function UserDetailDrawer({ userId, onClose, onChanged }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("profile");
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");

  useEffect(() => {
    if (!userId) { setData(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/users/${userId}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load user");
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const impersonate = async () => {
    if (!window.confirm(`Start a 15-minute impersonation session as ${data.user.email}?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${userId}/impersonate`);
      // Stash admin's own token so ImpersonationBanner can restore it on exit.
      const adminToken = localStorage.getItem("dp_token") || "";
      localStorage.setItem("dp_admin_token_backup", adminToken);
      localStorage.setItem("dp_token", r.data.access_token);
      localStorage.setItem("dp_impersonation", JSON.stringify({
        target_email: r.data.target.email,
        impersonated_by: r.data.impersonated_by,
        expires_at: new Date(Date.now() + r.data.expires_in_seconds * 1000).toISOString(),
      }));
      toast.success(`Piloting as ${r.data.target.email} for 15 minutes`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    } finally {
      setBusy(false);
    }
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/users/${userId}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dpp-user-export-${data.user.email}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async () => {
    if (!window.confirm(`HARD DELETE ${data.user.email}? This wipes their portfolios, holdings, broker link, subscriptions, everything. Cannot be undone.`)) return;
    if (!window.confirm("Are you absolutely sure?")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/admin/users/${userId}`);
      toast.success(`Deleted ${data.user.email} — wiped ${Object.keys(r.data.purged || {}).length} collections`);
      onClose();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const sendUserPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) { toast.error("Title + body required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/push-broadcast", {
        segment: "user", user_id: userId, title: pushTitle, body: pushBody,
      });
      if (r.data.users_notified === 0) {
        toast.error(`No push devices for this user (${r.data.candidates} candidate, ${r.data.pushes_failed} failed)`);
      } else {
        toast.success(`Push sent — ${r.data.pushes_sent} device(s)`);
      }
      setPushTitle(""); setPushBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="user-detail-drawer">
        {!data && (
          <div className="p-10 grid place-items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {data && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span>{data.user.email}</span>
                <Badge className="mono text-[10px] uppercase" variant={data.subscription.plan === "pro" ? "default" : "outline"}>
                  {data.subscription.plan}
                </Badge>
                {data.user.auth_provider !== "password" && (
                  <Badge variant="outline" className="mono text-[9px] uppercase">{data.user.auth_provider}</Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                Joined {fmtDate(data.user.created_at)} · {data.user.name || "no display name"} · {data.usage.portfolios_saved} portfolios · {data.push_subscription_count} push device(s)
              </DialogDescription>
            </DialogHeader>

            <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="profile" data-testid="user-tab-profile">Profile</TabsTrigger>
                <TabsTrigger value="usage" data-testid="user-tab-usage">Usage</TabsTrigger>
                <TabsTrigger value="portfolios" data-testid="user-tab-portfolios">Portfolios</TabsTrigger>
                <TabsTrigger value="actions" data-testid="user-tab-actions">Actions</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="overflow-y-auto flex-1 space-y-3 mt-3">
                <UserKV label="User ID"          value={data.user.id} mono />
                <UserKV label="Auth provider"    value={data.user.auth_provider} />
                <UserKV label="Last login"       value={fmtDate(data.user.last_login_at)} />
                <UserKV label="Referred by"      value={data.user.referred_by || "—"} />
                <UserKV label="Marketing consent" value={data.user.marketing_consent ? "Yes" : "No"} />
                <UserKV label="Newsletter"       value={data.user.newsletter_opt_in ? "Subscribed" : "Unsubscribed"} />
                <div className="border-t border-border pt-3 mt-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Subscription</div>
                  <UserKV label="Status"       value={data.subscription.status || "—"} />
                  <UserKV label="Renews / ends" value={fmtDate(data.subscription.current_period_end)} />
                  <UserKV label="Cancel at end" value={data.subscription.cancel_at_period_end ? "Yes" : "No"} />
                  <UserKV label="Stripe customer" value={data.subscription.stripe_customer_id || "—"} mono />
                </div>
                <div className="border-t border-border pt-3 mt-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">SnapTrade (broker)</div>
                  {data.snaptrade ? (
                    <>
                      <UserKV label="SnapTrade user ID" value={data.snaptrade.snaptrade_user_id || "—"} mono />
                      <UserKV label="Connected"   value={fmtDate(data.snaptrade.connected_at)} />
                      <UserKV label="Last sync"   value={fmtDate(data.snaptrade.last_sync_at)} />
                      <UserKV label="Sync state"  value={data.snaptrade.sync_state?.status || "—"} />
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground">Not connected</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="usage" className="overflow-y-auto flex-1 mt-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <UsageStat label="Portfolios saved"    value={data.usage.portfolios_saved} />
                  <UsageStat label="Backtests run"       value={data.usage.backtests_run} />
                  <UsageStat label="AI narratives"       value={data.usage.ai_narratives_generated} />
                  <UsageStat label="Chatbot messages"    value={data.usage.chatbot_messages} />
                  <UsageStat label="Contact messages"    value={data.usage.contact_messages_sent} />
                  <UsageStat label="Logins (30d)"        value={data.usage.logins_last_30d} />
                  <UsageStat label="Trial invites redeemed" value={data.usage.trial_invites_redeemed} />
                  <UsageStat label="Holdings accounts"   value={data.holdings.accounts} />
                  <UsageStat label="Holdings positions"  value={data.holdings.positions} />
                </div>
                {data.recent_activity && data.recent_activity.length > 0 && (
                  <div className="mt-6">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent activity (last 10)</div>
                    <div className="space-y-1">
                      {data.recent_activity.map((a, i) => (
                        <div key={i} className="text-xs mono text-foreground/80 border-b border-border/60 py-1 flex items-center justify-between gap-2">
                          <span>{a.action}</span>
                          <span className="text-muted-foreground">{fmtDate(a.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="portfolios" className="overflow-y-auto flex-1 mt-3">
                {data.portfolios.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No portfolios yet</div>
                ) : (
                  <div className="space-y-2">
                    {data.portfolios.map((p) => (
                      <div key={p.id} className="border border-border rounded-md p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-sm">{p.name}</div>
                          <span className="mono text-[10px] text-muted-foreground">{fmtDate(p.updated_at || p.created_at)}</span>
                        </div>
                        <div className="mono text-[10px] text-muted-foreground mt-1">
                          {(p.tickers || []).slice(0, 8).map((t) => t.ticker).join(" · ")}
                          {p.tickers && p.tickers.length > 8 && ` +${p.tickers.length - 8} more`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="actions" className="overflow-y-auto flex-1 space-y-4 mt-3">
                <div className="rounded-md border border-primary/30 bg-primary/[0.04] p-4">
                  <div className="serif text-lg mb-1 inline-flex items-center gap-2"><UserCog className="w-4 h-4 text-primary" /> Impersonate</div>
                  <p className="text-xs text-muted-foreground mb-3">Sign in as this user for 15 minutes to debug an issue they reported. Every action is audit-logged.</p>
                  <Button onClick={impersonate} disabled={busy} data-testid="user-impersonate-btn" className="rounded-full">
                    Start impersonation
                  </Button>
                </div>

                <div className="rounded-md border border-border p-4">
                  <div className="serif text-lg mb-1 inline-flex items-center gap-2"><Radio className="w-4 h-4" /> Send this user a push</div>
                  <p className="text-xs text-muted-foreground mb-3">Delivers immediately to their installed PWA device(s).</p>
                  <div className="grid gap-2">
                    <Input placeholder="Title (e.g. Your SCHD dividend just hit)" value={pushTitle} onChange={(e) => setPushTitle(e.target.value)} data-testid="user-push-title" />
                    <Textarea placeholder="Body (e.g. $34.20 landed in your Fidelity Roth this morning.)" rows={2} value={pushBody} onChange={(e) => setPushBody(e.target.value)} data-testid="user-push-body" />
                    <Button onClick={sendUserPush} disabled={busy} data-testid="user-push-send" className="rounded-full self-start">
                      <Send className="w-4 h-4 mr-2" /> Send
                    </Button>
                  </div>
                </div>

                <div className="rounded-md border border-border p-4">
                  <div className="serif text-lg mb-1 inline-flex items-center gap-2"><Download className="w-4 h-4" /> GDPR export</div>
                  <p className="text-xs text-muted-foreground mb-3">Everything this user has produced, as a JSON file. Fernet-encrypted broker secrets and push keys are redacted.</p>
                  <Button variant="outline" onClick={exportData} disabled={busy} data-testid="user-export-btn" className="rounded-full">
                    Download JSON
                  </Button>
                </div>

                <div className="rounded-md border border-destructive/40 bg-destructive/[0.04] p-4">
                  <div className="serif text-lg mb-1 inline-flex items-center gap-2 text-destructive"><Trash2 className="w-4 h-4" /> Right to be forgotten</div>
                  <p className="text-xs text-muted-foreground mb-3">Cascading delete — user row + portfolios + holdings + SnapTrade + subscriptions + activity + chatbot history. Cannot be undone.</p>
                  <Button variant="destructive" onClick={deleteUser} disabled={busy} data-testid="user-delete-btn" className="rounded-full">
                    Delete user + all data
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="ghost" onClick={onClose}><X className="w-4 h-4 mr-1" /> Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserKV({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm border-b border-border/40 py-1.5">
      <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`${mono ? "mono text-xs" : ""} text-right truncate`}>{value || "—"}</span>
    </div>
  );
}

function UsageStat({ label, value }) {
  return (
    <div className="rounded-md border border-border p-2.5">
      <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="serif text-xl mt-0.5">{fmt(value)}</div>
    </div>
  );
}
